"use client";

import Link from "next/link";
import { socials } from "@/lib/socials";
import {
  isDir,
  isFile,
  resolve,
  toAbsolute,
  UNAME_STRING,
  type Fs,
} from "@/components/heroes/terminal/terminal-fs";
import type { Line } from "@/components/heroes/terminal/terminal-render";

/**
 * Command registry + dispatch for the interactive shell. Pure
 * (no I/O, no React state) so it can be unit-tested in isolation;
 * the terminal component imports `runCommand`/`buildHelp`/etc.
 * and feeds the results into history state.
 *
 * Adding a new command is a one-place edit: add an entry to the
 * `KNOWN_COMMANDS` array (so Tab completion picks it up), add a
 * `case` in `runCommand`, and (optionally) add help text in
 * `buildCommandHelp`.
 */

/** Names of the commands this shell actually implements. Used by
 *  Tab completion on the first token and by the help screen. */
export const KNOWN_COMMANDS: readonly string[] = [
  "cat",
  "cd",
  "clear",
  "date",
  "echo",
  "help",
  "hostname",
  "ls",
  "pwd",
  "uname",
  "whoami",
];

/** Per-command help text (what `man <cmd>` would show). Plain
 *  string arrays so they flow through the same `Line` plumbing
 *  as the rest of the terminal. */
export function buildHelp(): string[] {
  return [
    "available commands:",
    "  cd <path>       change directory",
    "  pwd             print current directory",
    "  ls [path]       list files",
    "  whoami          print the current user",
    "  cat <file>      print a file",
    "  echo <text>     print text",
    "  date            print the current date/time",
    "  uname           print system info",
    "  hostname        print hostname",
    "  clear           clear the terminal",
    "  help            show this list",
  ];
}

export function buildCommandHelp(name: string): string[] {
  switch (name) {
    case "cd":
      return [
        "usage: cd <path>",
        "",
        "  change the working directory.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "pwd":
      return [
        "usage: pwd",
        "",
        "  print the current directory.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "ls":
      return [
        "usage: ls [path]",
        "",
        "  list files in the current or given directory.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "whoami":
      return [
        "usage: whoami",
        "",
        "  print the current user.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "cat":
      return [
        "usage: cat <file>",
        "",
        "  print a file.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "echo":
      return [
        "usage: echo <text>...",
        "",
        "  print text.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "date":
      return [
        "usage: date",
        "",
        "  print the current date and time.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "uname":
      return [
        "usage: uname [-a]",
        "",
        "  print system info.",
        "",
        "flags:",
        "  -a, --all    print all available system info",
        "  -h, --help   show this help",
      ];
    case "hostname":
      return [
        "usage: hostname",
        "",
        "  print the hostname.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "clear":
      return [
        "usage: clear",
        "",
        "  clear the terminal.",
        "",
        "flags:",
        "  -h, --help   show this help",
      ];
    case "help":
      return [
        "usage: help",
        "",
        "  show the list of available commands.",
        "",
        "flags:",
        "  -h, --help   show this list",
      ];
    default:
      return [`no help available for: ${name}`];
  }
}

/** Split a command line into words the way a POSIX shell does for
 *  the cases people actually type: whitespace separates words,
 *  `'...'` is literal, `"..."` allows `\"` / `\\` escapes, and a
 *  backslash outside quotes escapes the next character. `""` yields
 *  an empty word (so `--subject ""` works). */
export function tokenize(
  input: string
): { tokens: string[] } | { error: string } {
  const tokens: string[] = [];
  let cur = "";
  let inWord = false;
  let quote: '"' | "'" | null = null;
  for (let i = 0; i < input.length; i++) {
    const c = input[i];
    if (quote === "'") {
      if (c === "'") quote = null;
      else cur += c;
      continue;
    }
    if (quote === '"') {
      if (c === '"') quote = null;
      else if (c === "\\" && '"\\$`'.includes(input[i + 1] ?? "")) cur += input[++i];
      else cur += c;
      continue;
    }
    if (/\s/.test(c)) {
      if (inWord) tokens.push(cur);
      cur = "";
      inWord = false;
      continue;
    }
    inWord = true;
    if (c === "'" || c === '"') quote = c;
    else if (c === "\\" && i + 1 < input.length) cur += input[++i];
    else cur += c;
  }
  if (quote) return { error: `unexpected EOF while looking for matching \`${quote}'` };
  if (inWord) tokens.push(cur);
  return { tokens };
}

const CONTACT_SCRIPT = "~/contact.sh";

/** If the command line runs contact.sh (`./contact.sh`,
 *  `~/contact.sh`, `blog/../contact.sh`, `bash contact.sh`, ...),
 *  return the script's own arguments; otherwise `null`. Like a real
 *  shell, a bare `contact.sh` is a command lookup, not a path, so
 *  running the script directly needs a `/` in it. */
export function contactScriptArgs(tokens: string[], cwd: string): string[] | null {
  const [first, second] = tokens;
  if (first === undefined) return null;
  if ((first === "bash" || first === "sh") && second !== undefined) {
    return resolve(second, cwd) === CONTACT_SCRIPT ? tokens.slice(2) : null;
  }
  if (first.includes("/") && resolve(first, cwd) === CONTACT_SCRIPT) {
    return tokens.slice(1);
  }
  return null;
}

/** True if the first non-empty rest token is `-h` or `--help`. */
export function wantsHelp(rest: string[]): boolean {
  const first = rest.find((t) => t.length > 0);
  return first === "-h" || first === "--help";
}

export const CONTACT_REQUIRED_FLAGS = [
  "name",
  "email",
  "subject",
  "message",
] as const;

/** Parse contact.sh's arguments (already tokenized, script name
 *  stripped) into a key->value map, returning both the parsed map
 *  and the list of flag names that are missing (so the help message
 *  can list which ones are absent). */
export function parseContactArgs(args: string[]): {
  parsed: Partial<Record<(typeof CONTACT_REQUIRED_FLAGS)[number], string>>;
  unknownFlags: string[];
  missing: (typeof CONTACT_REQUIRED_FLAGS)[number][];
} {
  const parsed: Partial<
    Record<(typeof CONTACT_REQUIRED_FLAGS)[number], string>
  > = {};
  const unknownFlags: string[] = [];
  for (let i = 0; i < args.length; i += 2) {
    const flag = args[i];
    const value = args[i + 1];
    if (!flag || !flag.startsWith("--")) {
      // either a stray positional arg or a missing value
      continue;
    }
    const name = flag.slice(2) as (typeof CONTACT_REQUIRED_FLAGS)[number];
    if (value === undefined) {
      // odd number of args — flag without value
      continue;
    }
    if (!(CONTACT_REQUIRED_FLAGS as readonly string[]).includes(name)) {
      unknownFlags.push(name);
      continue;
    }
    parsed[name] = value;
  }
  const missing = CONTACT_REQUIRED_FLAGS.filter((f) => !(f in parsed));
  return { parsed, unknownFlags, missing };
}

/** Result of dispatching a single command. */
export type CmdResult = {
  out?: string[];
  err?: string[];
  newCwd?: string;
  clear?: boolean;
};

/** Run one command. Pure — takes the raw input, cwd, and fs,
 *  returns the structured result. The component is responsible
 *  for converting this into history lines and updating cwd. */
export function runCommand(rawCmd: string, cwd: string, fs: Fs): CmdResult {
  const parsed = tokenize(rawCmd);
  if ("error" in parsed) return { err: [`bash: ${parsed.error}`] };
  if (parsed.tokens.length === 0) return { out: [] };

  const [name, ...rest] = parsed.tokens;
  const arg = rest.join(" ");

  switch (name) {
    case "help":
      return { out: buildHelp() };
    case "pwd":
      if (wantsHelp(rest)) return { out: buildCommandHelp("pwd") };
      return { out: [toAbsolute(cwd)] };
    case "whoami":
      if (wantsHelp(rest)) return { out: buildCommandHelp("whoami") };
      return { out: ["emily"] };
    case "hostname":
      if (wantsHelp(rest)) return { out: buildCommandHelp("hostname") };
      return { out: ["byemily.org"] };
    case "uname": {
      if (wantsHelp(rest)) return { out: buildCommandHelp("uname") };
      if (arg === "-a" || arg === "--all") return { out: [UNAME_STRING] };
      return { out: ["Linux"] };
    }
    case "date":
      if (wantsHelp(rest)) return { out: buildCommandHelp("date") };
      return { out: [new Date().toString()] };
    case "echo":
      if (wantsHelp(rest)) return { out: buildCommandHelp("echo") };
      return { out: [arg] };
    case "clear":
      if (wantsHelp(rest)) return { out: buildCommandHelp("clear") };
      return { out: [], clear: true };
    case "ls": {
      if (wantsHelp(rest)) return { out: buildCommandHelp("ls") };
      // Anything starting with `-` is an unknown flag.
      if (rest.some((t) => t.startsWith("-"))) {
        return {
          err: [
            `ls: unrecognized option '${rest.find((t) => t.startsWith("-"))}'`,
            "try `ls --help` for the list of supported flags.",
          ],
        };
      }
      // Real `ls` prints nothing on an empty directory — no banner,
      // no "(empty)" — so an empty directory adds no lines. With
      // several directory arguments each listing gets a `name:`
      // header, like the real thing.
      const paths = rest.length > 0 ? rest : ["."];
      const out: string[] = [];
      const err: string[] = [];
      for (const p of paths) {
        const target = resolve(p, cwd);
        const entry = target === null ? undefined : fs[target];
        if (target === null) {
          err.push(`ls: cannot open directory '${p}': permission denied`);
        } else if (isFile(entry)) {
          out.push(target.split("/").pop() ?? target);
        } else if (isDir(entry)) {
          if (paths.length > 1) out.push(`${p}:`);
          out.push(...Object.keys(entry.children));
          if (paths.length > 1) out.push("");
        } else {
          err.push(`ls: cannot access '${p}': no such file or directory`);
        }
      }
      if (out[out.length - 1] === "") out.pop();
      return { out, err };
    }
    case "cd": {
      if (wantsHelp(rest)) return { out: buildCommandHelp("cd") };
      if (rest.length > 1) return { err: ["cd: too many arguments"] };
      const target = resolve(arg, cwd);
      if (target === null) {
        return { err: [`cd: ${arg}: permission denied (you're not allowed out of ~)`] };
      }
      const entry = fs[target];
      if (isDir(entry)) return { out: [], newCwd: target };
      if (isFile(entry)) return { err: [`cd: ${arg}: not a directory`] };
      return { err: [`cd: ${arg}: no such file or directory`] };
    }
    case "cat": {
      if (wantsHelp(rest)) return { out: buildCommandHelp("cat") };
      if (rest.length === 0) return { err: ["cat: missing file operand"] };
      const out: string[] = [];
      const err: string[] = [];
      for (const p of rest) {
        const target = resolve(p, cwd);
        const entry = target === null ? undefined : fs[target];
        if (target === null) err.push(`cat: ${p}: permission denied`);
        else if (isFile(entry)) out.push(...entry.split("\n"));
        else if (isDir(entry)) err.push(`cat: ${p}: is a directory`);
        else err.push(`cat: ${p}: no such file or directory`);
      }
      return { out, err };
    }
    default:
      return {
        err: [
          `command not found: ${name}`,
          `try \`help\` to see the list of things this shell knows about.`,
        ],
      };
  }
}

/** Inline help/error block shown when `./contact.sh` is run with
 *  bad flags. Returns a `Line[]` with mixed kinds (so the
 *  socials and `/contact` link render as clickable anchors).
 *  The terminal component flattens these into history the same
 *  way it does for normal output. */
export function buildContactHelp(
  missing: readonly string[],
  unknownFlags: readonly string[] = []
): Line[] {
  const out: Line[] = [];
  out.push({ kind: "err", text: "contact.sh: missing or invalid arguments" });
  if (missing.length > 0) {
    out.push({
      kind: "err",
      text: `  missing: ${missing.map((m) => `--${m}`).join(", ")}`,
    });
  }
  if (unknownFlags.length > 0) {
    out.push({
      kind: "err",
      text: `  unknown flag(s): ${unknownFlags.map((f) => `--${f}`).join(", ")}`,
    });
  }
  out.push({ kind: "out", text: "" });
  out.push({ kind: "out", text: "usage:" });
  out.push({
    kind: "out",
    text: "  ./contact.sh --name <n> --email <e> --subject <s> --message <m>",
  });
  out.push({
    kind: "out",
    text: '  all four flags are required (--subject may be empty, e.g. --subject "")',
  });
  out.push({ kind: "out", text: "" });
  // The mention of `/contact` here is itself a clickable link
  // (the standalone line below is just for emphasis / to make
  // the URL obvious in the output). Both forms land on the same
  // /contact page.
  out.push({
    kind: "md",
    nodes: [
      <span key="alt-prefix" className="text-[color:var(--color-muted)]">
        {"alternatively, use the "}
      </span>,
      <Link
        key="alt-link"
        href="/contact"
        className="text-[color:#5b81bb] underline decoration-dotted underline-offset-2 hover:text-[color:var(--color-accent)] hover:decoration-solid"
      >
        /contact
      </Link>,
      <span key="alt-suffix" className="text-[color:var(--color-muted)]">
        {" form"}
      </span>,
    ],
  });
  out.push({ kind: "out", text: "" });
  out.push({ kind: "out", text: "or reach me on:" });
  // Build a list of socials as inline links. Skip RSS (it's not a
  // person-to-person channel) and email (the /contact form already
  // covers that).
  for (const s of socials) {
    if (s.kind === "rss") continue;
    out.push({
      kind: "md",
      nodes: [
        <span key={`${s.kind}-prefix`} className="text-[color:var(--color-muted)]">
          {"  "}
        </span>,
        <span key={`${s.kind}-kind`} className="text-[color:var(--color-fg)]">
          {`${s.kind}: `}
        </span>,
        <Link
          key={`${s.kind}-link`}
          href={s.href}
          {...(s.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-[color:#5b81bb] underline decoration-dotted underline-offset-2 hover:text-[color:var(--color-accent)] hover:decoration-solid"
        >
          {s.handle}
        </Link>,
      ],
    });
  }
  return out;
}
